import { queryOptions, useQuery } from '@tanstack/react-query';

import { POLLING_DELAY_MS } from '@cast/constants';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

export const INVENTORY_ASSET_LIST_CLUSTERS_QUERY_KEY =
  'inventory-asset-list-clusters';

const getAssetListClustersQueryKey = (
  params: { organizationId?: string } = {}
) => [INVENTORY_ASSET_LIST_CLUSTERS_QUERY_KEY, params.organizationId] as const;

type QueryProps = {
  polling?: boolean;
  enabled?: boolean;
};

const getAssetListClustersQueryOptions = ({
  organizationId,
  polling = false,
  enabled = true,
}: {
  organizationId?: string;
  polling?: boolean;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getAssetListClustersQueryKey({ organizationId }),
    queryFn: async () => {
      const { data } = await apiClient.inventory.getAssetListClusters({
        organizationId: organizationId!,
      });
      return data.items.filter(
        // TODO (Justas): Remove this when BE sanitizes data on their end. This cluster ruins entire summary section
        (item) => item.name !== 'load-test-0819-dvoros-kwok'
      );
    },
    enabled: makeIsEnabled(!!organizationId, enabled),
    refetchInterval: polling ? POLLING_DELAY_MS : false,
  });

export const useAssetListClustersQuery = ({
  polling = false,
  enabled = true,
}: QueryProps = {}) => {
  const { currentOrganization } = useOrganizations();
  const organizationId = currentOrganization?.id;

  const { isLoading, data, error, refetch } = useQuery(
    getAssetListClustersQueryOptions({ organizationId, polling, enabled })
  );

  return {
    isLoading,
    assetListClusters: data,
    error,
    refetch,
  };
};
